import { useState, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { HelivanColor } from '../helivan/HelivanColor';
import { useDeleteMyAccountMutation } from '../../generated/graphql';
import { useAuth } from '../../contexts/AuthContext';

export const DangerZone = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleteMyAccount, { loading }] = useDeleteMyAccountMutation();
  const { logout } = useAuth();
  const toast = useToast();

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  const handleDelete = async () => {
    try {
      await deleteMyAccount();
      toast({
        title: 'Account deleted',
        description: 'Your account has been permanently deleted.',
        status: 'info',
        duration: 5000,
      });
      await logout();
    } catch (err) {
      toast({
        title: 'Failed to delete account',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        status: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <Box>
      <Box
        border="1px solid"
        borderColor="red.300"
        borderRadius="8px"
        p={5}
      >
        <Text fontWeight={600} fontSize="16px" color="red.600" mb={2}>
          Danger Zone
        </Text>

        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap={4}
        >
          <Box>
            <Text fontWeight={500} fontSize="14px" color={HelivanColor.text.black}>
              Delete your account
            </Text>
            <Text fontSize="13px" color={HelivanColor.text.gray} mt={1}>
              Permanently delete your account and all associated data including projects, agents, and organizations you own. This action cannot be undone.
            </Text>
          </Box>
          <Button
            colorScheme="red"
            variant="outline"
            size="sm"
            flexShrink={0}
            onClick={onOpen}
          >
            Delete Account
          </Button>
        </Flex>
      </Box>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Account
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={4}>
                This will <strong>permanently delete</strong> your account and all your data, including:
              </Text>
              <Box as="ul" pl={5} mb={4} fontSize="14px" color={HelivanColor.text.gray}>
                <li>All projects and project templates</li>
                <li>All custom agents</li>
                <li>All conversations</li>
                <li>Organizations you are the sole owner of</li>
              </Box>
              <Text mb={3} fontWeight={500} fontSize="14px">
                Type <strong>DELETE</strong> to confirm:
              </Text>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                size="sm"
              />
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleClose} size="sm">
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                size="sm"
                isDisabled={confirmText !== 'DELETE'}
                isLoading={loading}
              >
                Delete Account
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
